import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import './Legal.css';

const DMCA: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="legal-header">
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <div className="legal-title-section">
            <AlertTriangle size={48} className="legal-icon" />
            <h1>DMCA & Copyright Policy</h1>
            <p className="legal-subtitle">Last updated: January 2024</p>
          </div>
        </div>
      </div>

      <div className="legal-content">
        <div className="container">
          <div className="legal-body">
            <section>
              <h2>1. Copyright Protection</h2>
              <p>
                All content, source code, design, API endpoints, and documentation on Flazu API are protected under:
              </p>
              <ul>
                <li><strong>Digital Millennium Copyright Act</strong> (DMCA) - US Copyright Law</li>
                <li><strong>Berne Convention</strong> - International copyright treaties</li>
                <li><strong>Applicable Local Copyright Laws</strong> - As per your jurisdiction</li>
              </ul>
              <p>
                <strong>Copyright Owner:</strong> Flazu API<br />
                <strong>All Rights Reserved.</strong> Unauthorized copying, modification, or distribution is strictly prohibited.
              </p>
            </section>

            <section>
              <h2>2. Prohibited Activities</h2>
              <p>The following activities are <strong>STRICTLY PROHIBITED</strong> and constitute copyright infringement:</p>
              
              <h3>2.1 Code & Software</h3>
              <ul>
                <li>Copying, reproducing, or cloning our source code</li>
                <li>Reverse engineering, decompiling, or disassembling our software</li>
                <li>Creating derivative works based on our code</li>
                <li>Distributing or sublicensing our software</li>
                <li>Removing or modifying copyright notices</li>
              </ul>

              <h3>2.2 Content & Documentation</h3>
              <ul>
                <li>Copying documentation without attribution</li>
                <li>Scraping or harvesting content from our website</li>
                <li>Republishing our content on other platforms</li>
                <li>Using our branding, logos, or trademarks without permission</li>
              </ul>

              <h3>2.3 API & Services</h3>
              <ul>
                <li>Reselling or redistributing API access</li>
                <li>Creating competing services using our code</li>
                <li>Bypassing authentication or security measures</li>
                <li>Sharing API keys with unauthorized parties</li>
              </ul>
            </section>

            <section>
              <h2>3. Legal Consequences</h2>
              
              <h3>3.1 Civil Penalties</h3>
              <p>Copyright infringement may result in:</p>
              <ul>
                <li><strong>Statutory Damages:</strong> Significant financial penalties per infringement</li>
                <li><strong>Actual Damages:</strong> Compensation for losses suffered</li>
                <li><strong>Injunction:</strong> Court order to cease infringing activities</li>
                <li><strong>Legal Costs:</strong> Recovery of attorney fees and court costs</li>
              </ul>

              <h3>3.2 Criminal Penalties</h3>
              <p>Serious infringement may result in:</p>
              <ul>
                <li><strong>Unauthorized Access:</strong> Fines and/or imprisonment</li>
                <li><strong>Unauthorized Modification:</strong> Increased penalties for code tampering</li>
                <li><strong>Commercial Piracy:</strong> Severe penalties for profit-driven infringement</li>
              </ul>

              <h3>3.3 Additional Actions</h3>
              <ul>
                <li>Immediate account termination</li>
                <li>Permanent ban from all services</li>
                <li>Report to relevant authorities</li>
                <li>Domain takedown notices</li>
                <li>Hosting provider complaints</li>
                <li>Legal action in applicable jurisdictions</li>
              </ul>
            </section>

            <section>
              <h2>4. DMCA Takedown Procedure</h2>
              
              <h3>4.1 Filing a DMCA Notice</h3>
              <p>If you believe your copyrighted work has been infringed on our platform, submit a DMCA notice to:</p>
              <div style={{ background: 'var(--color-bg-secondary)', padding: '1.5rem', borderRadius: 'var(--radius-md)', marginTop: '1rem' }}>
                <p style={{ margin: 0 }}>
                  <strong>DMCA Agent:</strong> ChuyoRiko - Flazu API Legal Team<br />
                  <strong>Email:</strong> <a href="mailto:chuyo@riko.my">chuyo@riko.my</a><br />
                  <strong>Subject Line:</strong> "DMCA Takedown Notice"<br />
                </p>
              </div>

              <h3>4.2 Required Information</h3>
              <p>Your DMCA notice must include:</p>
              <ul>
                <li><strong>Identification of copyrighted work:</strong> Description or URL of your original work</li>
                <li><strong>Identification of infringing material:</strong> URL or location of the infringing content</li>
                <li><strong>Contact information:</strong> Name, address, phone, email</li>
                <li><strong>Good faith statement:</strong> "I have a good faith belief that use of the material is not authorized"</li>
                <li><strong>Accuracy statement:</strong> "I swear, under penalty of perjury, that this information is accurate"</li>
                <li><strong>Signature:</strong> Physical or electronic signature</li>
              </ul>

              <h3>4.3 DMCA Notice Template</h3>
              <div style={{ background: 'var(--color-bg-secondary)', padding: '1.5rem', borderRadius: 'var(--radius-md)', marginTop: '1rem', fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
{`To: chuyo@riko.my
Subject: DMCA Takedown Notice

I am the copyright owner (or authorized agent) of:
[Describe your copyrighted work]

The following material infringes my copyright:
Infringing URL: [URL of infringing material]
Original Work: [URL of your original work]

I have a good faith belief that the use of this material is not authorized by the copyright owner, its agent, or the law.

I swear, under penalty of perjury, that the information in this notification is accurate and that I am the copyright owner or authorized to act on behalf of the owner.

Electronic Signature: [Your Name]
Name: [Full Name]
Company: [If applicable]
Address: [Your Address]
Phone: [Your Phone]
Email: [Your Email]
Date: [Current Date]`}
              </div>
            </section>

            <section>
              <h2>5. Counter-Notice Procedure</h2>
              <p>
                If you believe your content was removed in error, you may file a DMCA counter-notice.
              </p>
              
              <h3>5.1 Counter-Notice Requirements</h3>
              <p>Your counter-notice must include:</p>
              <ul>
                <li>Your physical or electronic signature</li>
                <li>Identification of removed material and its location</li>
                <li>Statement under penalty of perjury that removal was a mistake</li>
                <li>Your name, address, phone number</li>
                <li>Consent to jurisdiction of Malaysian courts</li>
              </ul>

              <h3>5.2 Counter-Notice Process</h3>
              <ol>
                <li>Submit counter-notice to: <a href="mailto:chuyo@riko.my">chuyo@riko.my</a></li>
                <li>We forward your counter-notice to the original complainant</li>
                <li>If no lawsuit filed within 10-14 business days, we may restore content</li>
                <li>All disputes subject to applicable international laws</li>
              </ol>
            </section>

            <section>
              <h2>6. Repeat Infringer Policy</h2>
              <p>
                We maintain a <strong>zero-tolerance policy</strong> for repeat copyright infringers.
              </p>
              <ul>
                <li><strong>First Offense:</strong> Warning + content removal</li>
                <li><strong>Second Offense:</strong> Temporary account suspension (30 days)</li>
                <li><strong>Third Offense:</strong> Permanent account termination + legal action</li>
              </ul>
              <p>
                Users with multiple DMCA complaints will be permanently banned and may face legal prosecution.
              </p>
            </section>

            <section>
              <h2>7. Reporting Infringement of Our Content</h2>
              <p>
                If you find unauthorized copies of Flazu API content elsewhere, please report to us:
              </p>
              
              <h3>Report Details Should Include:</h3>
              <ul>
                <li>URL of infringing website/repository</li>
                <li>Description of stolen content (code, docs, design)</li>
                <li>Screenshots or evidence</li>
                <li>Your contact information (optional)</li>
              </ul>

              <div style={{ background: 'var(--color-bg-secondary)', padding: '1.5rem', borderRadius: 'var(--radius-md)', marginTop: '1rem' }}>
                <p style={{ margin: 0 }}>
                  <strong>Report To:</strong><br />
                  Email: <a href="mailto:chuyo@riko.my">chuyo@riko.my</a><br />
                  Subject: "Copyright Infringement Report"<br />
                </p>
              </div>

              <p style={{ marginTop: '1rem' }}>
                We will investigate and take appropriate action including:
              </p>
              <ul>
                <li>DMCA takedown notices to hosting providers</li>
                <li>GitHub repository removal requests</li>
                <li>Domain suspension requests</li>
                <li>Legal action if warranted</li>
              </ul>
            </section>

            <section>
              <h2>8. Fair Use Disclaimer</h2>
              <p>
                Limited use may be permitted under fair use/fair dealing exceptions:
              </p>
              <ul>
                <li><strong>Educational purposes:</strong> Teaching, research, study (with attribution)</li>
                <li><strong>Criticism/Review:</strong> Commentary with proper attribution</li>
                <li><strong>News reporting:</strong> Factual reporting with attribution</li>
              </ul>
              <p>
                <strong>Note:</strong> Fair use does NOT permit commercial use, redistribution, or creation of derivative works.
              </p>
            </section>

            <section>
              <h2>9. Contact Information</h2>
              <div style={{ background: 'var(--color-bg-secondary)', padding: '1.5rem', borderRadius: 'var(--radius-md)' }}>
                <p><strong>Flazu API Legal Department</strong></p>
                <p style={{ margin: '0.5rem 0' }}>
                  <strong>Contact Person:</strong> ChuyoRiko<br />
                  <strong>DMCA Notices:</strong> <a href="mailto:chuyo@riko.my">chuyo@riko.my</a><br />
                  <strong>Copyright Infringement:</strong> <a href="mailto:chuyo@riko.my">chuyo@riko.my</a><br />
                  <strong>General Legal:</strong> <a href="mailto:chuyo@riko.my">chuyo@riko.my</a><br />
                  <strong>Support:</strong> <a href="mailto:chuyo@riko.my">chuyo@riko.my</a>
                </p>
                <p style={{ margin: '1rem 0 0 0', fontSize: '0.875rem', opacity: 0.8 }}>
                  Response Time: 24-48 hours for DMCA notices<br />
                  Business Hours: Monday-Friday, 9AM-5PM UTC+8
                </p>
              </div>
            </section>

            <section>
              <h2>10. Related Policies</h2>
              <ul>
                <li><Link to="/terms">Terms of Service</Link> - Full terms and conditions</li>
                <li><Link to="/privacy">Privacy Policy</Link> - How we handle your data</li>
                <li><Link to="/cookies">Cookie Policy</Link> - Cookie usage information</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DMCA;